// Client-side API wrapper. Reads csrf cookie, attaches X-CSRF-Token,
// throws ApiError on non-2xx, returns JSON otherwise.
//
// Offline behavior: mutation requests on the allowlist
// (see lib/offlineQueue.QUEUEABLE_PATHS) get enqueued in IndexedDB if
// the network is unreachable. The caller sees an `ApiClientError` with
// code 'queued_offline' instead of a network error, and the queue
// drains on the next `online` event.

import type { ApiError } from '@shared/types';
import { enqueueMutation, shouldOfflineEnqueue, QUEUEABLE_PATHS } from './offlineQueue';
import { captureException } from './telemetry';

export class ApiClientError extends Error {
  constructor(public status: number, public code: string, public issues?: unknown) {
    super(`${status} ${code}`);
    this.name = 'ApiClientError';
  }
}

// Error codes that mean the server has invalidated this session. The
// client cookie is still present and looks valid locally, but every
// mutation will keep failing until the user signs in fresh. AuthContext
// subscribes to the 'auth:revoked' event below and flips to guest.
const SESSION_DEAD_CODES = new Set([
  'unauthenticated',
  'revoked',
  'session_revoked',
  'idle_too_long',
]);

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

function isQueueable(method: string, path: string): boolean {
  if (method === 'GET') return false;
  return QUEUEABLE_PATHS.some((p) => path === p || path.startsWith(p));
}

interface ApiOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export async function api<T = unknown>(path: string, opts: ApiOpts = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  let body: BodyInit | undefined;
  let serializedBody: string | null = null;
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    serializedBody = JSON.stringify(opts.body);
    body = serializedBody;
  }
  if (method !== 'GET') {
    const csrf = readCookie('csrf');
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      body,
      credentials: 'same-origin',
      signal: opts.signal,
    });
  } catch (err) {
    // Network failure: enqueue if this mutation is on the allowlist,
    // then throw a typed `queued_offline` error. Returning undefined
    // here silently masked failed POSTs as success (the caller's await
    // resolved cleanly, success toast fired, but the row never landed
    // server-side). Callers use isQueuedOffline(err) to distinguish
    // "queued for later" from real failures and surface the right toast.
    if (isQueueable(method, path) && shouldOfflineEnqueue(err)) {
      try {
        await enqueueMutation({
          path,
          method: method as 'POST' | 'PUT' | 'DELETE',
          body: serializedBody,
        });
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('queue:enqueued', { detail: { count: 1 } }));
        }
        throw new ApiClientError(0, 'queued_offline');
      } catch (enqueueErr) {
        if (enqueueErr instanceof ApiClientError) throw enqueueErr;
        // IDB write itself failed — fall through to a normal network error
      }
    }
    throw err;
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      // ignore — non-JSON body (export endpoint, etc.)
    }
  }
  if (!res.ok) {
    const err = (json as ApiError) ?? { error: 'http_error' };
    const code = err.error ?? 'http_error';
    // Report 5xx (and 0/unknown) to telemetry. 4xx are expected client
    // errors (validation, auth, rate limits) that the UI handles; we don't
    // want them flooding the issue tracker. Fire-and-forget; never block
    // the caller.
    if (res.status >= 500) {
      captureException(
        new Error(`api_5xx: ${res.status} ${code}`),
        { path, method, status: res.status, code, issues: err.issues ?? null },
      );
    }
    // Session-killing 401s: the SPA's React state may still say "authed"
    // because AuthContext only validates on initial mount. Fire a global
    // event so AuthContext can flip to 'guest' and route the user back
    // to login. Without this, an idle PWA (Android keeps the WebView warm
    // for days) leaves the user clicking "Log dose" against a dead
    // session forever, with no signal to re-auth.
    if (res.status === 401 && SESSION_DEAD_CODES.has(code) && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:revoked', { detail: { code } }));
    }
    throw new ApiClientError(res.status, code, err.issues);
  }
  return json as T;
}

// Convenience helpers
export const apiGet = <T = unknown>(p: string, signal?: AbortSignal) =>
  api<T>(p, { signal });
export const apiPost = <T = unknown>(p: string, body?: unknown) =>
  api<T>(p, { method: 'POST', body });
export const apiPut = <T = unknown>(p: string, body?: unknown) =>
  api<T>(p, { method: 'PUT', body });
export const apiPatch = <T = unknown>(p: string, body?: unknown) =>
  api<T>(p, { method: 'PATCH', body });
// DELETE accepts an optional body for endpoints that take a confirm
// payload (e.g. /api/auth/account, /api/me/push-subscription).
export const apiDelete = <T = void>(p: string, body?: unknown) =>
  api<T>(p, { method: 'DELETE', body });
